import React, { useEffect, useState } from 'react';
import { collection, onSnapshot, doc, updateDoc, writeBatch, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { Cpu, Check, X, Trash2, Usb, KeyRound } from 'lucide-react';
import { app } from '../firebase';

// Sección del panel de administración para los ESP32 registrados desde la app
// de escritorio "RochaRamas Flasher". Documentos en esp32Devices/{uid} (uno
// por usuario) e índice esp32Macs/{mac} para evitar duplicados.

const formatearMac = (mac) => (mac || '').match(/.{1,2}/g)?.join(':').toUpperCase() || '—';
const macParaNube = (mac) => String(mac || '').replace(/[^0-9a-fA-F]/g, '').toUpperCase();
const fecha = (ts) => (ts?.toDate ? ts.toDate().toLocaleString() : '—');

const ESTILO_ESTADO = {
  pendiente: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20 animate-pulse',
  aprobado: 'bg-green-500/10 text-green-400 border-green-500/20',
  rechazado: 'bg-red-500/10 text-red-400 border-red-500/20',
};

const AdminEsp32 = ({ db }) => {
  const [dispositivos, setDispositivos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [licenciando, setLicenciando] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'esp32Devices'), orderBy('creadoEn', 'desc'));
    return onSnapshot(
      q,
      (snap) => {
        setDispositivos(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setCargando(false);
      },
      (err) => {
        console.error('Error cargando ESP32', err);
        setError('No se pudieron cargar los ESP32 (revisa las reglas de Firestore).');
        setCargando(false);
      }
    );
  }, [db]);

  const cambiarEstado = async (d, estado) => {
    let motivoRechazo = '';
    if (estado === 'rechazado') {
      motivoRechazo = window.prompt('Motivo del rechazo (se mostrará al usuario):', '') ?? null;
      if (motivoRechazo === null) return;
    }
    try {
      await updateDoc(doc(db, 'esp32Devices', d.id), {
        estado,
        motivoRechazo: motivoRechazo || '',
        aprobadoEn: estado === 'aprobado' ? serverTimestamp() : null,
        actualizadoEn: serverTimestamp(),
      });
      // Al aprobar, emitimos ya su licencia: así el usuario puede instalar el
      // firmware desde el grabador sin depender de que estemos disponibles.
      if (estado === 'aprobado') await emitirLicencia(d);
    } catch (err) {
      console.error('Error cambiando estado del ESP32', err);
      alert('No se pudo cambiar el estado: ' + err.message);
    }
  };

  // Llama a la Cloud Function que firma con la llave privada. La licencia solo
  // sirve en el chip cuya MAC firma, así que guardarla en Firestore es seguro.
  const emitirLicencia = async (d) => {
    setLicenciando(d.id);
    try {
      const firmar = httpsCallable(getFunctions(app, 'us-central1'), 'firmarLicencia');
      const { data } = await firmar({ mac: macParaNube(d.mac) });
      if (!data?.licencia) throw new Error('La nube no devolvió licencia.');
      setAviso(`Licencia emitida para ${formatearMac(d.mac)}.`);
    } catch (err) {
      console.error('Error emitiendo licencia', err);
      setAviso('');
      alert(
        'El ESP32 quedó aprobado, pero no se pudo emitir su licencia: ' +
          (err.message || err) +
          '. Comprueba que las funciones firmarLicencia y firmarReto estén desplegadas.'
      );
    } finally {
      setLicenciando(null);
    }
  };

  // Borra el dispositivo y libera la MAC para que el usuario pueda registrar de nuevo.
  const eliminar = async (d) => {
    if (!window.confirm(`¿Eliminar el ESP32 ${formatearMac(d.mac)} de ${d.email}? El usuario podrá volver a registrarlo.`)) return;
    try {
      const batch = writeBatch(db);
      batch.delete(doc(db, 'esp32Devices', d.id));
      if (d.mac) batch.delete(doc(db, 'esp32Macs', d.mac));
      await batch.commit();
    } catch (err) {
      console.error('Error eliminando ESP32', err);
      alert('No se pudo eliminar: ' + err.message);
    }
  };

  const pendientes = dispositivos.filter((d) => d.estado === 'pendiente').length;

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Cpu size={16} className="text-indigo-400" />
        <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">ESP32 registrados</span>
        {pendientes > 0 && (
          <span className="bg-yellow-500/20 text-yellow-400 text-[10px] px-2 py-0.5 rounded border border-yellow-500/30 font-black uppercase tracking-wider">
            {pendientes} pendiente{pendientes > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {error && <div className="text-red-400 text-sm mb-3">{error}</div>}
      {aviso && <div className="text-green-400 text-sm mb-3">{aviso}</div>}

      {cargando ? (
        <div className="text-slate-500 text-sm">Cargando...</div>
      ) : dispositivos.length === 0 ? (
        <div className="text-slate-500 text-sm border border-dashed border-slate-700 rounded-xl p-4 text-center">
          Ningún ESP32 registrado todavía. Los usuarios los registran desde la app de escritorio RochaRamas Flasher.
        </div>
      ) : (
        <div className="grid gap-2">
          {dispositivos.map((d) => (
            <div key={d.id} className="bg-slate-700/30 rounded-xl border border-slate-600 p-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 border ${ESTILO_ESTADO[d.estado] || ESTILO_ESTADO.pendiente}`}>
                  <Usb size={18} />
                </div>
                <div className="min-w-0">
                  <div className="text-white font-bold flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-indigo-300 tracking-wider">{formatearMac(d.mac)}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded border font-black uppercase tracking-wider ${ESTILO_ESTADO[d.estado] || ''}`}>
                      {d.estado}
                    </span>
                  </div>
                  <div className="text-xs text-slate-400 truncate">{d.email} · {d.chip || 'chip desconocido'}{d.flashSize ? ` · ${d.flashSize}` : ''}</div>
                  <div className="text-xs text-slate-500">
                    Solicitado: {fecha(d.creadoEn)}
                    {d.descripcion ? ` · ${d.descripcion}` : ''}
                    {d.firmwareInstalado?.version ? ` · Firmware v${d.firmwareInstalado.version} (${fecha(d.firmwareInstalado.fecha)})` : ' · Sin firmware instalado'}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {d.estado !== 'aprobado' && (
                  <button
                    onClick={() => cambiarEstado(d, 'aprobado')}
                    className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-bold"
                    title="Aprobar ESP32"
                  >
                    <Check size={16} /> Aprobar
                  </button>
                )}
                {d.estado !== 'rechazado' && (
                  <button
                    onClick={() => cambiarEstado(d, 'rechazado')}
                    className="flex items-center gap-1 px-3 py-1.5 bg-slate-700 hover:bg-red-500/20 text-red-400 rounded-lg text-sm font-bold border border-transparent hover:border-red-500/30"
                    title="Rechazar ESP32"
                  >
                    <X size={16} /> Rechazar
                  </button>
                )}
                <button
                  onClick={() => emitirLicencia(d)}
                  disabled={licenciando === d.id}
                  className="p-2 bg-slate-700 text-indigo-300 hover:bg-indigo-500/20 rounded-lg border border-transparent hover:border-indigo-500/30 disabled:opacity-40"
                  title="Emitir o renovar la licencia de esta máquina"
                >
                  <KeyRound size={18} />
                </button>
                <button
                  onClick={() => eliminar(d)}
                  className="p-2 bg-slate-700 text-red-400 hover:bg-red-500/20 hover:text-red-300 rounded-lg border border-transparent hover:border-red-500/30"
                  title="Eliminar y liberar la MAC"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminEsp32;
